import { queryOptions, useQuery } from '@tanstack/react-query';

import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';
import { ServiceAccountWithGroups } from '@cast/features-organization-shared-types';

import { overrideOrganizationHeader } from '@cast/api-client';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

export const SERVICE_ACCOUNTS_QUERY_KEY = 'service-accounts';

const getServiceAccountsQueryKey = (params: { organizationId?: string }) =>
  [SERVICE_ACCOUNTS_QUERY_KEY, params.organizationId] as const;

const getServiceAccountsQueryOptions = ({
  organizationId,
  enabled = true,
}: {
  organizationId?: string;
  enabled?: boolean;
} = {}) =>
  queryOptions({
    queryKey: getServiceAccountsQueryKey({ organizationId }),
    queryFn: async (): Promise<ServiceAccountWithGroups[]> => {
      const { data } = await apiClient.serviceAccounts.getServiceAccounts(
        {
          organizationId: organizationId!,
        },
        {
          headers: overrideOrganizationHeader(organizationId),
        }
      );

      return (data.serviceAccounts ?? []).map(
        (sa): ServiceAccountWithGroups => ({
          id: sa.id ?? '',
          name: sa.name,
          email: sa.email ?? '',
          description: sa.description ?? '',
          createdAt: sa.createdAt ?? '',
          managedBy: sa.managedBy ?? '',
          keysCount: sa.keysCount ?? 0,
          organizationId: sa.organizationId ?? '',
          author: {
            id: sa.author?.id ?? '',
            email: sa.author?.email ?? '',
          },
        })
      );
    },
    enabled: makeIsEnabled(!!organizationId, enabled),
  });

type UseGetServiceAccountsQueryProps = {
  enabled?: boolean;
  organizationId?: string;
};

export const useGetServiceAccountsQuery = ({
  enabled = true,
  organizationId,
}: UseGetServiceAccountsQueryProps = {}) => {
  const { currentOrganization: organization } = useOrganizations();
  const resolvedOrganizationId = organizationId ?? organization?.id;

  const query = useQuery(
    getServiceAccountsQueryOptions({
      organizationId: resolvedOrganizationId,
      enabled,
    })
  );

  return { serviceAccounts: query.data, ...query };
};
